import * as React from "react";

type AsChildElement = React.ReactElement<{ children?: React.ReactNode }>;

/** The single element `asChild` swaps in, or null when there isn't a usable
 *  one. Used directly by the wrappers that render through `useRender` instead
 *  of a Base UI part (Button, Label). */
export function asChildElement(
  asChild: boolean | undefined,
  children: React.ReactNode,
): AsChildElement | null {
  return asChild &&
    React.isValidElement<{ children?: React.ReactNode }>(children)
    ? children
    : null;
}

/** Radix `asChild` → Base UI `render`: the child element replaces the element
 *  the part would render. `decorate` re-wraps the part's own markup (icons,
 *  indicators) and is cloned in, because a render element's children win. */
export function asChildProps(
  asChild: boolean | undefined,
  children: React.ReactNode,
  decorate?: (content: React.ReactNode) => React.ReactNode,
): { render: React.ReactElement } | { children: React.ReactNode } {
  const child = asChildElement(asChild, children);

  if (!child) {
    return { children: decorate ? decorate(children) : children };
  }

  return {
    render: decorate
      ? React.cloneElement(child, undefined, decorate(child.props.children))
      : child,
  };
}
